/**
 * PreferenceHeatMeter
 *
 * Visual flame scale for spice tolerance (0-5 flames)
 * Animated fill on mount with tooltip for full description.
 */
import { Box, HStack, Text, VStack, Tooltip } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { Flame } from 'lucide-react'
import type { SpiceTolerance } from '@/types/tasteInsights'
import { getSpiceLevelLabel, formatScoville } from './preferenceUtils'
import { PreferenceCardHeader } from './PreferenceCardHeader'

const MotionBox = motion.create(Box)

interface PreferenceHeatMeterProps {
  /** Structured spice tolerance data */
  value: SpiceTolerance
  /** Maximum number of flames (default 5) */
  maxLevel?: number
}

export function PreferenceHeatMeter({ value, maxLevel = 5 }: PreferenceHeatMeterProps) {
  const level = value.level
  const label = getSpiceLevelLabel(level)
  const scovilleDisplay = formatScoville(value.scovilleMax)

  // Build tooltip content
  const tooltipContent = (
    <VStack align="start" spacing={1}>
      <Text>{value.description}</Text>
      {value.scovilleMax > 0 && (
        <Text fontSize="xs" color="whiteAlpha.600">
          Up to {scovilleDisplay} Scoville
        </Text>
      )}
    </VStack>
  )

  return (
    <Tooltip
      label={tooltipContent}
      placement="top"
      hasArrow
      bg="gray.800"
      color="white"
      fontSize="xs"
      px={3}
      py={2}
      borderRadius="var(--r-chip)"
      maxW="280px"
    >
      <Box
        bg="var(--card-surface-2)"
        borderRadius="var(--r-card-compact)"
        p={3}
        h="100%"
        minH="88px"
        cursor="pointer"
        _hover={{ bg: 'var(--card-chip-hover)' }}
        transition="background 0.2s"
      >
        <VStack align="stretch" spacing={2}>
          {/* Row 1: Header */}
          <PreferenceCardHeader icon={Flame} label="Spice Tolerance" color="orange.400" />

          {/* Row 2: Value */}
          <Text fontSize="sm" fontWeight="medium" color="var(--card-text)" textAlign="center">
            {label}
          </Text>

          {/* Row 3: Flame meter */}
          <HStack spacing={1} justify="center">
            {Array.from({ length: maxLevel }).map((_, index) => {
              const isActive = index < level
              return (
                <MotionBox
                  key={index}
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                  }}
                  transition={{
                    delay: index * 0.1,
                    duration: 0.3,
                    ease: 'easeOut',
                  }}
                >
                  <Box
                    p={1.5}
                    borderRadius="var(--r-chip)"
                    bg={isActive ? 'orange.500' : 'var(--card-chip-hover)'}
                    color={isActive ? 'white' : 'var(--card-chip-border)'}
                    transition="all 0.3s"
                  >
                    <Flame
                      size={18}
                      fill={isActive ? 'currentColor' : 'none'}
                    />
                  </Box>
                </MotionBox>
              )
            })}
          </HStack>
        </VStack>
      </Box>
    </Tooltip>
  )
}
